import { Button, DeleteIcon, Select, SelectOptionType } from '@bp/ui-components';
import { Form, Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateNumbersSelectValues } from '../../../hooks/useCreateNumbersSelectValues';
import {
  SubjectConstraints,
  subjectConstraintFields,
  subjectConstraintLabels,
} from '../../../hooks/useSubjectConstraints';
import styles from '../Constraints.module.scss';
import { ConstraintPriorities } from '../types';
import { NumberPriorityConstraintItem } from './FormItems/NumberPriorityConstraintItem';
import { PriorityIconSelect } from './FormItems/PriorityIconSelect';

type SubjectConditionsFormProps = {
  constraints: SubjectConstraints;
  onSubmit: (blob: SubjectConstraints) => Promise<boolean>;
  onClose: () => void;
};

type FormValues = {
  maxPerDay: number | null;
  maxPerDayPriority: ConstraintPriorities;
  maxDaysPerWeek: number | null;
  maxDaysPerWeekPriority: ConstraintPriorities;
  edgeOfDay: boolean;
  edgeOfDayPriority: ConstraintPriorities;
};

const EDGE_OF_DAY = 'edgeOfDay';

export const SubjectConditionsForm = ({ constraints, onSubmit, onClose }: SubjectConditionsFormProps) => {
  const { t } = useTranslation();
  const labels = subjectConstraintLabels(t);
  const numberOptions = useCreateNumbersSelectValues();

  const ranges = useMemo(
    () => ({
      posPerDay: numberOptions.filter((o) => (o.value as number) >= 1),
      perWeek: numberOptions.filter((o) => (o.value as number) >= 1 && (o.value as number) <= 7),
      fromZero: numberOptions,
    }),
    [numberOptions],
  );

  const initialValues: FormValues = useMemo(
    () => ({
      maxPerDay: constraints.maxPerDay ?? null,
      maxPerDayPriority: constraints.maxPerDayPriority ?? 'OPTIMIZING',
      maxDaysPerWeek: constraints.maxDaysPerWeek ?? null,
      maxDaysPerWeekPriority: constraints.maxDaysPerWeekPriority ?? 'OPTIMIZING',
      edgeOfDay: constraints.edgeOfDay ?? false,
      edgeOfDayPriority: constraints.edgeOfDayPriority ?? 'OPTIMIZING',
    }),
    [constraints],
  );

  // Which constraints are currently shown — seeded from the ones the subject already has.
  const [selected, setSelected] = useState<string[]>([]);
  useEffect(() => {
    setSelected([
      ...subjectConstraintFields.filter((d) => constraints[d.field] != null).map((d) => d.field),
      ...(constraints.edgeOfDay ? [EDGE_OF_DAY] : []),
    ]);
  }, [constraints]);

  const addOptions: SelectOptionType[] = [
    ...subjectConstraintFields.map((d) => ({ value: d.field, label: labels[d.field].label })),
    { value: EDGE_OF_DAY, label: t('conditions.subject.edgeOfDay') },
  ].filter((o) => !selected.includes(o.value as string));

  const handleSubmit = async (values: FormValues) => {
    const blob: SubjectConstraints = {};
    for (const descriptor of subjectConstraintFields) {
      if (!selected.includes(descriptor.field)) continue;
      const value = values[descriptor.field];
      if (typeof value === 'number') {
        blob[descriptor.field] = value;
        blob[descriptor.priorityField] = values[descriptor.priorityField] ?? 'OPTIMIZING';
      }
    }
    if (selected.includes(EDGE_OF_DAY) && values.edgeOfDay) {
      blob.edgeOfDay = true;
      blob.edgeOfDayPriority = values.edgeOfDayPriority ?? 'OPTIMIZING';
    }

    const success = await onSubmit(blob);
    if (success) onClose();
  };

  return (
    <Formik initialValues={initialValues} enableReinitialize onSubmit={handleSubmit}>
      {({ dirty, isSubmitting, resetForm, setFieldValue, setFieldTouched }) => {
        const removeField = (field: string) => {
          setSelected(selected.filter((s) => s !== field));
          if (field === EDGE_OF_DAY) {
            setFieldValue(EDGE_OF_DAY, false);
            setFieldValue('edgeOfDayPriority', 'OPTIMIZING');
          } else {
            setFieldValue(field, null);
            const descriptor = subjectConstraintFields.find((d) => d.field === field);
            if (descriptor) setFieldValue(descriptor.priorityField, 'OPTIMIZING');
          }
          setFieldTouched(field, true);
        };
        return (
          <Form className={styles['conditions-form']}>
            <Select
              isTrigger
              className={styles['master-select']}
              placeholder={t('conditions.pleaseSelect')}
              options={addOptions}
              name='addCondition'
              menuPosition='fixed'
              onChange={(value) => {
                const field = (value as SelectOptionType).value as string;
                setSelected([field, ...selected]);
                if (field === EDGE_OF_DAY) {
                  setFieldValue(EDGE_OF_DAY, true);
                  setFieldTouched(EDGE_OF_DAY, true);
                }
              }}
              label={t('conditions.add')}
            />
            <div className='tks__grid no-gap'>
              {subjectConstraintFields
                .filter((descriptor) => selected.includes(descriptor.field))
                .map((descriptor) => (
                  <NumberPriorityConstraintItem
                    key={descriptor.field}
                    label={labels[descriptor.field].label}
                    tooltipText={labels[descriptor.field].tooltip}
                    numberName={descriptor.field}
                    priorityName={descriptor.priorityField}
                    numberOptions={ranges[descriptor.range]}
                    onRemove={() => removeField(descriptor.field)}
                  />
                ))}
              {selected.includes(EDGE_OF_DAY) && (
                <div className={styles['condition-form-item']}>
                  <div className={styles['row-wrapper']}>
                    <div className='tks__row row-xs-11 mr-5' style={{ flex: 1, alignItems: 'center' }}>
                      <div className='tks__col col-xs-12'>{t('conditions.subject.edgeOfDay')}</div>
                    </div>
                    <div className='tks__col col-xs-1'>
                      <PriorityIconSelect name='edgeOfDayPriority' />
                    </div>
                    <div className={styles['button-wrapper']}>
                      <Button
                        name='remove'
                        hierarchy='secondary'
                        onClick={() => removeField(EDGE_OF_DAY)}
                        icon={<DeleteIcon />}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className='modal-bottom'>
              <Button
                hierarchy='tertiary'
                type='button'
                onClick={() => {
                  resetForm();
                  onClose();
                }}
              >
                {t('common.cancelChanges')}
              </Button>
              <Button type='submit' disabled={isSubmitting || !dirty}>
                {t('common.save')}
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
